import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import theme from '../theme';
import MarkdownContent from '../CompanyPage/MarkdownContent';
import EditWrapper from '../CompanyPage/EditWrapper';
import ItemSwitcher from '../components/ItemSwitcher';
import useQueryParam from '../components/useQueryParam';
import QuestionAnswer from '../../components/QuestionAnswer/QuestionAnswer';
import useUnsavedGuard from '../components/useUnsavedGuard';
import { expandQuery, textMatches, matchRanges } from '../../utils/searchTerms';
import {
  PageWrapper, PageHeader, PageTitle, EditButton, EmptyState, Card, SubCardTitle, PageSkeleton,
} from '../CompanyPage/CompanyPage.styles';

// An objection as one line of text: its markdown flattened.
export const plainText = (markdown) => String(markdown || '').replace(/[#*_>`]/g, '').replace(/\s+/g, ' ').trim();

// The matched words wrapped in <mark>; the text itself when there is nothing
// to match. Takes the typed query or an already expanded one, and marks
// whichever synonyms or word forms actually hit (utils/searchTerms).
export const highlight = (text, needleOrExpanded) => {
  const expanded = typeof needleOrExpanded === 'string' ? expandQuery(needleOrExpanded) : (needleOrExpanded || []);
  const ranges = matchRanges(text, expanded);
  if (ranges.length === 0) return text;
  const parts = [];
  let from = 0;
  ranges.forEach(([start, end]) => {
    if (start > from) parts.push(text.slice(from, start));
    parts.push(<Mark key={start}>{text.slice(start, end)}</Mark>);
    from = end;
  });
  if (from < text.length) parts.push(text.slice(from));
  return parts;
};

export const SEARCH_DEBOUNCE_MS = 150;
const pluralize = (n, noun) => `${n} ${noun}${n === 1 ? '' : 's'}`;
const ALL = 'all';
const tagKey = (t) => `tag:${t}`;

// Objection Handling, view mode. The same rail as the other business-data
// pages, but the rail holds the SECTIONS (the "related to" tags, plus "All
// objections") with the search box above them, and the right side shows
// every objection in the chosen section with its response in full. Nothing
// to click through; the search narrows what is shown and the rail counts
// follow it. Section and search live in the URL (?section=Offerings&q=pricing).
const QuestionsAnswersPage = observer(({ company, mode }) => {
  const [editing, setEditing] = useState(false);
  const { onDirtyChange, confirmLeave } = useUnsavedGuard();
  const [sectionParam, setSectionParam] = useQueryParam('section', ALL);
  const [qParam, setQParam] = useQueryParam('q', '');
  const [query, setQuery] = useState(qParam);
  const [needleRaw, setNeedleRaw] = useState(qParam);
  const isReadonly = mode === 'readonly';
  const loaded = !!company?.id;
  const questions = company?.questionsAndAnswers || [];

  // Typing updates the box at once; the filtering, the rail counts and the URL
  // follow a moment later, so 64 cards are not re-rendered on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setNeedleRaw(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);
  // The search box writes the URL; it must NOT also react to the URL, or an
  // outside change (sidebar click, back button) makes the two effects swap
  // values forever. setQParam is a no-op when the URL already matches.
  useEffect(() => {
    setQParam(needleRaw, { replace: true });
  }, [needleRaw, setQParam]);
  // The URL changed from outside (back button): follow it.
  useEffect(() => {
    if (qParam !== needleRaw) {
      setQuery(qParam);
      setNeedleRaw(qParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qParam]);

  // Not memoised on `questions`: the MST array keeps its identity across a
  // save (applySnapshot reconciles in place), so a memo never saw a new tag.
  // At most a few dozen items; recomputing per render is nothing.
  const tags = [];
  questions.forEach((qa) => { if (qa?.related_to && !tags.includes(qa.related_to)) tags.push(qa.related_to); });

  const needle = needleRaw.trim().toLowerCase();
  // Synonyms and word forms, every query word satisfied somewhere in the
  // objection or its response (utils/searchTerms).
  const expanded = useMemo(() => expandQuery(needle), [needle]);
  const matches = (qa) => !needle || textMatches(`${plainText(qa.question)} ${plainText(qa.answer)}`, expanded);
  const inSection = (qa, key) => key === ALL || qa.related_to === key.slice(4);

  // The rail: "All objections" first, then one row per tag when there is more
  // than one tag to choose between. Captions carry the counts, narrowed by the
  // search so the rail says where the matches are.
  const caption = (total, found) => (needle ? `${found} of ${total} match` : pluralize(total, 'objection'));
  const sections = [{ key: ALL, label: 'All objections', caption: caption(questions.length, questions.filter(matches).length) }];
  if (tags.length > 1) {
    tags.forEach((t) => {
      const total = questions.filter((qa) => qa.related_to === t);
      sections.push({ key: tagKey(t), label: t, caption: caption(total.length, total.filter(matches).length) });
    });
  }
  const requestedKey = sectionParam === ALL ? ALL : tagKey(sectionParam);
  const activeKey = sections.some((s) => s.key === requestedKey) ? requestedKey : ALL;

  if (editing) {
    return (
      <PageWrapper>
        <PageHeader>
          <PageTitle>Objection Handling</PageTitle>
        </PageHeader>
        <EditWrapper onBack={() => { if (confirmLeave()) setEditing(false); }}>
          <QuestionAnswer company={company} onDirtyChange={onDirtyChange} />
        </EditWrapper>
      </PageWrapper>
    );
  }

  const toolbar = (
    <SearchInput
      type="search"
      aria-label="Search objections"
      placeholder="Search objections and responses"
      value={query}
      onChange={(e) => setQuery(e.target.value)}
    />
  );

  const renderDetail = (section) => {
    const inThis = questions.filter((qa) => inSection(qa, section.key));
    const shown = inThis.filter(matches);
    return (
      <>
        <Showing>
          {needle
            ? `${shown.length} of ${pluralize(inThis.length, 'objection')} match "${needleRaw.trim()}"`
            : pluralize(inThis.length, 'objection')}
        </Showing>
        {shown.length === 0 ? (
          <EmptyState>No objections match your search.</EmptyState>
        ) : (
          <ObjectionList>
            {shown.map((qa) => <ObjectionCard key={questions.indexOf(qa)} qa={qa} needle={expanded} />)}
          </ObjectionList>
        )}
      </>
    );
  };

  return (
    <PageWrapper data-tour="qa-page">
      <PageHeader>
        <PageTitle>Objection Handling</PageTitle>
        {loaded && !isReadonly && (
          <EditButton onClick={() => setEditing(true)}>
            {'✎'} Edit
          </EditButton>
        )}
      </PageHeader>

      {!loaded ? (
        <PageSkeleton>
          <div style={{ flex: '0 0 280px', height: 260 }} />
          <div style={{ flex: 1, height: 420 }} />
        </PageSkeleton>
      ) : questions.length === 0 ? (
        <EmptyState>No objections have been added yet.</EmptyState>
      ) : (
        <ItemSwitcher
          items={sections}
          activeKey={activeKey}
          onChange={(key) => setSectionParam(key === ALL ? ALL : key.slice(4))}
          renderDetail={renderDetail}
          ariaLabel="Objection sections"
          toolbar={toolbar}
          frame="plain"
          position={false}
          pager={false}
        />
      )}
    </PageWrapper>
  );
});

// The response is markdown and the heaviest thing on the page; it does not
// change while someone types, so it is rendered once per objection.
const Response = React.memo(({ markdown }) => <MarkdownContent markdown={markdown} />);

const ObjectionCard = React.memo(({ qa, needle }) => (
  <Card as="article">
    <ObjectionText>{highlight(plainText(qa.question) || 'Untitled objection', needle)}</ObjectionText>
    <SubCardTitle>Response</SubCardTitle>
    <Response markdown={qa.answer} />
  </Card>
));

const SearchInput = styled.input`
  width: 100%;
  min-width: 0;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.body.size};
  color: ${theme.colors.text};
  background: ${theme.colors.surface};
  border: 1.5px solid ${theme.colors.border};
  border-radius: ${theme.radii.sm};
  padding: 10px 12px;
  min-height: 44px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px ${theme.colors.primaryGhost};
  }
`;

const Showing = styled.div`
  font-size: ${theme.typography.small.size};
  font-weight: 500;
  color: ${theme.colors.textSecondary};
  margin-top: -${theme.spacing.sm};
`;

const ObjectionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const ObjectionText = styled.h3`
  margin: 0 0 ${theme.spacing.md};
  font-size: 15px;
  font-weight: 600;
  line-height: 1.5;
  color: ${theme.colors.text};
  overflow-wrap: anywhere;
`;

const Mark = styled.mark`
  background: rgba(7, 132, 167, 0.18);
  color: inherit;
  border-radius: 2px;
  padding: 0 2px;
`;

export default QuestionsAnswersPage;
